import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook to handle language changes and force re-renders
 * This ensures all components update when language changes
 */
export const useLanguageChange = () => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng);
      setForceUpdate(prev => prev + 1);
    };

    const handleCustomLanguageChange = (event) => {
      const { language } = event.detail;
      handleLanguageChange(language);
    };

    const handleStorageChange = () => {
      const storedLanguage = localStorage.getItem('I18N_LANGUAGE');
      if (storedLanguage && storedLanguage !== currentLanguage) {
        handleLanguageChange(storedLanguage);
      }
    };

    // Listen for language changes from i18n
    i18n.on('languageChanged', handleLanguageChange);

    // Listen for custom language change events
    window.addEventListener('languageChanged', handleCustomLanguageChange);

    // Listen for storage changes
    window.addEventListener('storage', handleStorageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      window.removeEventListener('languageChanged', handleCustomLanguageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [i18n, currentLanguage]);

  const changeLanguage = (languageCode) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('I18N_LANGUAGE', languageCode);
  };

  return {
    currentLanguage,
    changeLanguage,
    forceUpdate
  };
};

export default useLanguageChange; 